import type { AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import type { Endpoint } from './endpoints';

/**
 * `Endpoint` 를 받는 얇은 axios 래퍼 — request 함수들은 raw client 대신 이걸 쓴다(app-legacy 와 동일 패턴).
 *
 * 역할은 하나: 엔드포인트의 **경로를 URL 로 풀고, 정책(`requiresAuth`·`retryAfterTokenRefresh`)을 요청
 * config 에 실어 인터셉터까지 전달**한다. 인터셉터는 URL 목록을 매칭하지 않고 `config.endpoint` 만 읽는다.
 *
 * 엔드포인트가 **필수 위치 인자**라는 게 핵심 — raw client 를 직접 쓰면 config 의 `endpoint` 를 빠뜨려도
 * 컴파일은 통과하고 런타임에만 조용히 망가진다(정책 미인식 → 토큰 미부여 → 401, 재시도도 없음).
 *
 * 호출부 표기는 raw client 와 같다 — `http.get(endpoints.plant.byId(plantId), { signal })`.
 */
export function createHttp(client: AxiosInstance) {
  const cfg = (endpoint: Endpoint, config?: AxiosRequestConfig) => ({ ...config, endpoint });
  return {
    get: <T>(e: Endpoint, config?: AxiosRequestConfig) => client.get<T>(e.path, cfg(e, config)),
    post: <T>(e: Endpoint, data?: unknown, config?: AxiosRequestConfig) =>
      client.post<T>(e.path, data, cfg(e, config)),
    put: <T>(e: Endpoint, data?: unknown, config?: AxiosRequestConfig) =>
      client.put<T>(e.path, data, cfg(e, config)),
    patch: <T>(e: Endpoint, data?: unknown, config?: AxiosRequestConfig) =>
      client.patch<T>(e.path, data, cfg(e, config)),
    delete: <T>(e: Endpoint, config?: AxiosRequestConfig) =>
      client.delete<T>(e.path, cfg(e, config)),
  };
}

/** request 함수가 받는 타입 — 구현에서 추론(시그니처를 인터페이스에 한 번 더 적지 않는다). */
export type Http = ReturnType<typeof createHttp>;

/**
 * 인터셉터가 읽는 요청 config — axios 는 미지의 필드를 config 에 그대로 실어 통과시키고 `error.config`
 * 로도 되돌려준다(`_retried` 와 동일 메커니즘).
 */
export interface EndpointRequestConfig extends InternalAxiosRequestConfig {
  /** `http` 가 실어 보낸 엔드포인트 정책. 래퍼를 안 거친 요청엔 없다(= 정책 미선언 → 무인증·무재시도 취급). */
  endpoint?: Endpoint;
  /** 재발급 후 1회 재시도했음 — 재-401 시 세션 확정 만료 판정용. */
  _retried?: boolean;
}
